/* External */
import { FormattedMessage } from 'react-intl';

/* Ott-common */
import { SectionId } from '@dtvgo/backend-service/normalize';
import { FORGEROCK_ACCOUNT_TYPES } from '@dtvgo/utils';

/* Other */
import { getJsonImage } from 'src/theme/images';

/* Assets */
import oneByOneImage from '~/assets/theme/images/1_1.png';
import twoByThreeImage from '~/assets/theme/images/2_3.png';
import fourByThreeImage from '~/assets/theme/images/4_3.png';
import sixteenByNineImage from '~/assets/theme/images/16_9.png';
import welcomePageGenericImage from '~/assets/images/welcomePageGeneric.png';

/* Utils */
import { Routes } from './router';
import {
  BITMOVIN_ANALYTICS_ENABLED,
  BITMOVIN_ANALYTICS_KEY,
  DEFAULT_COUNTRY,
  DEFAULT_LICENSE_REQUEST_RETRIES,
  DEFAULT_LICENSE_REQUEST_TIMEOUT,

  LL_BUFFER_BACKWARD,
  LL_BUFFER_FORWARD,
  LL_TARGET_LATENCY,
  LL_CATCHUP_PLAYBACK_RATE,
  LL_CATCHUP_PLAYBACK_RATE_THRESHOLD,
  LL_CATCHUP_SEEK_THRESHOLD,
  LL_FALLBACK_PLAYBACK_RATE,
  LL_FALLBACK_PLAYBACK_RATE_THRESHOLD,
  LL_FALLBACK_SEEK_THRESHOLD,
  MS_TO_WAIT_ON_RECONNECTION_DEFAULT,
  MS_TO_WAIT_ON_RECONNECTION_FIRETV,
  MS_TO_WAIT_ON_RECONNECTION_HISENSE,
  MS_TO_WAIT_ON_RECONNECTION_TIZEN,
  MS_TO_WAIT_ON_RECONNECTION_WEBOS,
  MS_TO_WAIT_ON_RECONNECTION_ZEASN,
  MS_TO_WAIT_ON_RECONNECTION_TITAN,
  MS_TO_WAIT_ON_RECONNECTION_VEGAOS,
  PAGE_PROFILE,
  PAGE_HOME,
  PAGE_KIDS,
  PAGE_LIVE,
  PAGE_LIVE_NEW_EPG,
  PAGE_PRESEARCH,
  PAGE_SPORTS,
  PAGE_VOD,
  PLAYER_REQUEST_BACKOFF_FACTOR,
  PLAYER_REQUEST_BASE_DELAY,
  PLAYER_REQUEST_CONNECTION_TIMEOUT,
  PLAYER_REQUEST_FUZZ_FACTOR,
  PLAYER_REQUEST_MAX_ATTEMPTS,
  PLAYER_REQUEST_STALL_TIMEOUT,
  PLAYER_REQUEST_TIMEOUT,
  WEB_URL,
  LIVE_REBUFFERING_GOAL,
  VOD_REBUFFERING_GOAL,
  OTT_SSLA_BASE_URL,
  OTT_DTH_TVE_SSLA_BASE_URL,
  SKYMAIS_BASE_URL,
  UPSELL_URL_OTT_BRASIL,
  UPSELL_URL_DTH_TVE_BRASIL,
  TAPPP_ENV,
  pageMyPage,
} from './env';

/**
 * @enum {string}
 * @readonly
 */
export const Platform = {
  WEBOS: 'WEBOS',
  TIZEN: 'TIZEN',
  HISENSE: 'HISENSE',
  ZEASN: 'ZEASN',
  DEFAULT: 'DEFAULT',
  FIRETV: 'FIRETV',
  TITAN: 'TITAN',
  VEGAOS: 'VEGAOS',
};

export const WEB_URL_TO_DISPLAY = WEB_URL.replace('https://', '');
export const LANDER_LOGO_BASE_IMAGE_URL = `${WEB_URL}/content/dam/ott/web/stations`;

export const PageType = {
  PROFILE: PAGE_PROFILE,
  HOME: PAGE_HOME,
  VOD: PAGE_VOD,
  SPORTS: PAGE_SPORTS,
  LIVE: PAGE_LIVE,
  LIVE_NEW_EPG: PAGE_LIVE_NEW_EPG,
  KIDS: PAGE_KIDS,
  SEARCH: PAGE_PRESEARCH,
  MY_PAGE: pageMyPage,
};

export const RoutesOriginCarousels = {
  [PAGE_HOME]: Routes.Private.Home,
  [PAGE_VOD]: Routes.Private.OnDemand,
  [PAGE_SPORTS]: Routes.Private.Sports,
  [PAGE_LIVE]: Routes.Private.Live,
  [PAGE_KIDS]: Routes.Private.Kids,
  [pageMyPage]: Routes.Private.MyPage,
};

export const bitmovinAnalyticsKey = BITMOVIN_ANALYTICS_KEY;

export const isBitmovinAnalyticsEnabled = BITMOVIN_ANALYTICS_ENABLED;

export const MsToWaitOnReconnection = {
  [Platform.DEFAULT]: MS_TO_WAIT_ON_RECONNECTION_DEFAULT,
  [Platform.WEBOS]: MS_TO_WAIT_ON_RECONNECTION_WEBOS,
  [Platform.TIZEN]: MS_TO_WAIT_ON_RECONNECTION_TIZEN,
  [Platform.HISENSE]: MS_TO_WAIT_ON_RECONNECTION_HISENSE,
  [Platform.ZEASN]: MS_TO_WAIT_ON_RECONNECTION_ZEASN,
  [Platform.FIRETV]: MS_TO_WAIT_ON_RECONNECTION_FIRETV,
  [Platform.TITAN]: MS_TO_WAIT_ON_RECONNECTION_TITAN,
  [Platform.VEGAOS]: MS_TO_WAIT_ON_RECONNECTION_VEGAOS,
};

export const STATUS_TYPES = {
  UNLOAD_STATE: 'notLoaded',
  INIT_STATE: 'initState',
  SUCCEED_STATE: 'succeedState',
  FAILED_STATE: 'failedState',
};

export const ImageRatio = {
  ONE_BY_ONE: '1:1',
  FOUR_BY_THREE: '4:3',
  SIXTEEN_BY_NINE: '16:9',
  TWO_BY_THREE: '2:3',
};

export const defaultImages = {
  [ImageRatio.ONE_BY_ONE]: getJsonImage('1-1') || oneByOneImage,
  [ImageRatio.TWO_BY_THREE]: getJsonImage('2-3') || twoByThreeImage,
  [ImageRatio.FOUR_BY_THREE]: getJsonImage('4-3') || fourByThreeImage,
  [ImageRatio.SIXTEEN_BY_NINE]: getJsonImage('16-9') || sixteenByNineImage,
};

export const DEFAULT_SEPARATOR = ' • ';
export const DOT_SEPARATOR = ' • ';
export const SPACE = '\u00A0';

export const DEFAULT_MAX_CARDS_BY_SWIMLANE = 20;

/**
 * @enum {string}
 * @readonly
 */
export const Country = {
  ARGENTINA: 'ar',
  BRAZIL: 'br',
  CHILE: 'cl',
  COLOMBIA: 'co',
  ECUADOR: 'ec',
  MEXICO: 'mx',
  PERU: 'pe',
  URUGUAY: 'uy',
};

/**
         * @enum {string}
 * @readonly
 */
export const Locale = {
  EN: 'en',
  ES: 'es',
  PT: 'pt',
};

export const FONT_BASE = 20;
export const UHD_WIDTH_BASE = 1920;

export const MILLISECONDS_TO_DISPLAY_LOGIN_SUCCESS = (
  import.meta.env.VITE_MILLISECONDS_TO_DISPLAY_LOGIN_SUCCESS
);

export const TIME_FORMAT_FOR_SCHEDULES = 'HH:mm';
export const TIME_INTL_FORMAT_FOR_SCHEDULES = {
  hour: '2-digit',
  minute: '2-digit',
};

export const PROVIDERS = {
  VRIO: 'Vrio',
  SPIDEO: 'Spideo',
};

export const SECTIONS = {
  FEATURED: 'FEATURED',
  SIGUEVIENDO: 'SIGUEVIENDO',
  SIGUEVIENDOLIVE: 'SIGUEVIENDOLIVE',
  MILISTA: 'MILISTA',
  PRESEARCH: 'PRESEARCH',
};

export const SCHEMAS_CAROUSELS = {
  BANNERS: 'banners',
  CONTENTS: 'contents',
  SHOWS: 'shows',
  CHANNELS: 'channels',
  EMPTY: 'empty',
};

export const CollectionText = {
  PAGE: 'PAGE',
  SPORT_PAGE: 'SPORT_PAGE',
  SPORT_SECTION: 'SPORT_SECTION',
};

export const SPORT_PAGE_SET = new Set([CollectionText.SPORT_PAGE, CollectionText.SPORT_SECTION]);
export const LANDER_ACTION_TEXT_SET = new Set([CollectionText.PAGE]);

/**
 * @enum {string}
 * @readonly
 */
export const DrmKeySystem = {
  WIDEVINE: 'WIDEVINE',
  PLAYREADY: 'PLAYREADY',
};

export const REPORT_GA = {
  GA_EVENT_LABEL_NA: 'n/a',
  GA_EVENT_ACTION_ACTIVATION_SUCCESS: 'Activation success',
  GA_EVENT_ACTION_CLOSE: 'Close',
  GA_EVENT_ACTION_PIN_SUCCESS: 'PIN success',
  GA_EVENT_ACTION_PIN_ERROR: 'PIN error',
  GA_EVENT_ACTION_INTENTION: 'intention',
  GA_EVENT_ACTION_SUCCESS: 'Success',
  GA_EVENT_ACTION_IGNORE: 'Ignore',
  GA_EVENT_ACTION_BUTTON_CLICK: 'Button Click',
  GA_EVENT_CATEGORY_DEVICE_ACTIVATION: 'Device Activation',
  GA_EVENT_CATEGORY_LOGOUT: 'Logout',
  GA_EVENT_CATEGORY_INTERSTITIAL: 'Promo Interstitial Page',
  GA_EVENT_ACTION_PAGE_VIEW: 'Page View',
  GA_EVENT_ACTION_LANDER: 'Lander',
  GA_EVENT_ACTION_EPG_FILTER: 'epg_filter_click',
  GA_EVENT_ACTION_MINI_EPG_FILTER: 'mini_epg_filter_click',
  GA_EVENT_CATEGORY_PAGE_NAVIGATION: 'Navigation',
  GA_EVENT_CATEGORY_CONTENT: 'Content',
  GA_EVENT_LABEL_BEFORE_EVENT_INTERSTITIAL: 'Before event',
  GA_EVENT_LABEL_AFTER_EVENT_INTERSTITIAL: 'After event',
  GA_EVENT_LABEL_HOME: 'Home',
  GA_EVENT_LABEL_LIVE: 'Live',
  GA_EVENT_LABEL_ON_DEMAND: 'VOD',
  GA_EVENT_LABEL_SPORTS: 'Sports',
  GA_EVENT_LABEL_KIDS: 'Kids',
  GA_EVENT_CATEGORY_EPG: 'EPG',
  GA_EVENT_CATEGORY_MINI_EPG: 'mini-EPG',
  GA_EVENT_CATEGORY_SEARCH: 'Search',
  GA_EVENT_SEARCH: 'search_event',
  GA_EVENT_SEARCH_CONVERSION_PDP: 'search_conversion_pdp',
  GA_EVENT_SEARCH_CONVERSION_PLAY: 'search_conversion_play',
  GA_EVENT_SEARCH_ABANDON: 'search_abandon',
  GA_EVENT_ACTION_BACK_TO_START_FROM_PLAYER_BUTTON_CLICK: 'vod_player_watch_from_start_button_click',
  GA_EVENT_LABEL_BACK_TO_START_FROM_PLAYER_BUTTON_CLICK: 'User clicks on "Volver a Empezar" button',
  GA_EVENT_CATEGORY_VOD_PLAYER: 'vod_player',
  GA_EVENT_EVENT_PLAYER_CONTROLS_SHOWS: 'vod_player_control_shows',
  GA_EVENT_LABEL_PLAYER_CONTROLS_SHOWS: 'User is watching content and display player controls using Magic control or D-Pad',
  GA_ERROR_EVENT_ACTION_DRM_AGENT: 'Error loading DRM Agent',
  GA_ERROR_EVENT_ACTION_ERROR_RECEIVED: 'Error received',
  GA_ERROR_EVENT_CATEGORY: 'Error',
  GA_ERROR_EVENT_CATEGORY_ENTITLEMENTS: 'Entitlement error',
  GA_ERROR_EVENT_CATEGORY_BLACKOUT: 'Blackout error',
  GA_ERROR_EVENT_CATEGORY_CONCURRENCY: 'Concurrency error',
  GA_ERROR_EVENT_CATEGORY_PLAYBACK: 'Playback error',
  GA_ERROR_EVENT_CATEGORY_SIMULTANEOUS_PLAYBACK: 'Simultaneous Playback error',
  GA_ERROR_EVENT_CATEGORY_PIN_REATTEMPTS: 'PIN reattempts error',
  GA_ERROR_EVENT_CATEGORY_RENEW_TOKEN: 'Renew token error',
  GA_ERROR_EVENT_LABEL_BEFORE_EVENT_INTERSTITIAL: 'Before event (no access)',
  GA_ERROR_EVENT_LABEL_AFTER_EVENT_INTERSTITIAL: 'After event (no access)',
  // PDP
  GA_EVENT_ACTION_PDP_VIEW_PDP_PAGE: 'pdp_view_pdp_page',
  GA_EVENT_ACTION_PDP_WATCH_FROM_START_CLICK: 'pdp_watch_from_start_click',
  GA_EVENT_ACTION_PDP_RESUME_CONTENT_CLICK: 'pdp_resume_content_click',
  GA_EVENT_ACTION_PDP_WATCH_NOW_CLICK: 'pdp_watch_now_click',
  GA_EVENT_LABEL_PDP_VIEW_PDP_PAGE: 'User views PDP page',
  GA_EVENT_LABEL_PDP_WATCH_FROM_START_CLICK: 'User clicks on "Ver desde el inicio" button',
  GA_EVENT_LABEL_PDP_RESUME_CONTENT_CLICK: 'User clicks on "Reanudar TxEx" button',
  GA_EVENT_LABEL_PDP_WATCH_NOW_CLICK: 'User clicks on "Ver Ahora" button ',
  GA_EVENT_CATEGORY_PDP: 'PDP',
  // open app
  GA_EVENT_CATEGORY_OPEN_APP: 'open',
  GA_EVENT_ACTION_OPEN_APP: 'open_app',
  GA_EVENT_LABEL_OPEN_APP: 'user opens DTVGO App',
  // open mini-epg
  GA_EVENT_CATEGORY_OPEN_MINI_EPG: 'mini_epg',
  GA_EVENT_ACTION_OPEN_MINI_EPG: 'mini_epg_opened',
  GA_EVENT_LABEL_OPEN_MINI_EPG_ICON_BUTTON: 'mini_epg_opened_ok_button',
  GA_EVENT_LABEL_OPEN_MINI_EPG_CH_UP: 'mini_epg_opened_channel_up_button',
  GA_EVENT_LABEL_OPEN_MINI_EPG_CH_DOWN: 'mini_epg_opened_channel_down_button',
};

export const EpgQualifier = {
  NEW: 'epgQualifierNew',
  LIVE: 'epgQualifierLive',
  REPEAT: 'epgQualifierRepeat',
  PREMIERE: 'epgQualifierPremiere',
  SEASON_PREMIERE: 'epgQualifierSeasonPremiere',
  SERIES_PREMIERE: 'epgQualifierSeriesPremiere',
  SEASON_FINALE: 'epgQualifierSeasonFinale',
  SERIES_FINALE: 'epgQualifierSeriesFinale',
};

/**
 * @enum {string}
 * @readonly
 */
export const ErrorType = {
  LOAD_DRM_AGENT: 'LOAD_DRM_AGENT',
};

export const TIME_VALUES = {
  SECONDS_BY_HOUR: 3600,
  SECONDS_BY_MINUTE: 60,
  MILLISECONDS_BY_SECOND: 1000,
};

export const MINUTES_AFTER_CONSECUTIVE_EPISODES_VOD = 2;

export const ERRORS = {
  sessionError: {
    paramValue: 'sessionError', // This value must be the same than parent object key
    titleTranslationId: 'error.sessionError.title',
    descriptionTranslationId: 'error.sessionError.description',
    textButtonTranslationId: 'common.accept',
    redirectToUrl: '/',
    gaEventCategory: REPORT_GA.GA_ERROR_EVENT_CATEGORY_RENEW_TOKEN,
  },
};

export const PLAYER_TYPES = {
  SHAKA: 'SHAKA',
  BITMOVIN: 'BITMOVIN',
};

export const DESCRIPTIVE_AUDIO = {
  LANG: {
    PORTUGUESE: 'qad',
    SPANISH: 'qae',
    ENGLISH: 'qaf',
  },
  NAME: {
    PORTUGUESE: 'Portuguese',
    SPANISH: 'Spanish',
    ENGLISH: 'English',
  },
  ISO: {
    PORTUGUESE: 'pt',
    SPANISH: 'es',
    ENGLISH: 'en',
  },
};

export const SOURCE_CAROUSEL_ID = {
  CONTINUE_WATCHING: 'UCTAContinueWatching',
  RECENTLY_WATCHED: 'UCTARecentlyWatchedChannels',
  MY_LIST: 'UCTAMyListVODMoviesSeries',
  ROW_ADS: 'rowads-banner',
  HOME_BANNER: 'home-banner',
  TOAST_BANNER: 'toast-banner',
  TA_VOD: 'UCTAMiDGORecommendedVODMoodPeliculasSeries',
};

export const RECENTLY_WATCHED_PARAMS = {
  sourceCarouselId: SOURCE_CAROUSEL_ID.RECENTLY_WATCHED,
  source: 'ThinkAnalytics',
  type: 'recently-watched',
};

export const MY_LIST_PARAMS = {
  SOURCE_CAROUSEL_ID: SOURCE_CAROUSEL_ID.MY_LIST,
  SOURCE: 'ThinkAnalytics',
  TYPE: 'my-list',
};

export const TA_VOD_PARAMS = {
  SOURCE_CAROUSEL_ID: SOURCE_CAROUSEL_ID.TA_VOD,
  SOURCE: 'ThinkAnalytics',
  TYPE: 'ta-vod',
};

export const EPG_FILTERS_TYPES = {
  ALL: 'all',
  EVERY_CATEGORIES: 'every_categories',
  DATE: 'date',
  SECTION: 'section',
  CATEGORY: 'category',
  FOR_YOU: 'for-you',
};

export const EPG_FILTERS_ID = {
  ALL_STATIONS: 'all',
  DATE: 'date',
  MOST_WATCHED: SectionId.MOST_WATCHED,
  RECENTLY_WATCHED: SOURCE_CAROUSEL_ID.RECENTLY_WATCHED,
  FOR_YOU: 'for-you',
};

export const DEFAULT_AEM_CONFIG = {
  content: {
    epg: {
      numberOfFavoriteChannels: 3,
    },
    live: {
      liveWatching: 4,
    },
    miniepg: {
      recentlyWatchedLimit: 4,
    },
    vod: {
      vodWatching: 2,
    },
  },
  lockIcon: false,
  player: {
    audioAndVideoSync: {
      automaticallySyncAudioAndVideo: false,
      minutesOfContinuousPlayback: 30,
    },
    bitmovinAnalytics: {
      enable: isBitmovinAnalyticsEnabled,
      key: bitmovinAnalyticsKey,
    },
    delayToShowLostConection: '12000',
    logsLevel: 'WARN',
    orbis: {
      liveVideoBufferBackwardDuration: 12,
      liveVideoBufferForwardDuration: 30,
      // TODO: Request to include this field in config endpoint
      liveRebufferingGoal: LIVE_REBUFFERING_GOAL,
      vodVideoBufferBackwardDuration: 30,
      vodVideoBufferForwardDuration: 60,
      // TODO: Request to include this field in config endpoint
      vodRebufferingGoal: VOD_REBUFFERING_GOAL,
    },
    // TODO: Request to include this field in config endpoint
    licenseRequest: {
      retries: DEFAULT_LICENSE_REQUEST_RETRIES,
      tiemout: DEFAULT_LICENSE_REQUEST_TIMEOUT,
    },
    // TODO: Request to include this field in config endpoint
    requestConfig: {
      timeout: PLAYER_REQUEST_TIMEOUT,
      stallTimeout: PLAYER_REQUEST_STALL_TIMEOUT,
      connectionTimeout: PLAYER_REQUEST_CONNECTION_TIMEOUT,
      maxAttempts: PLAYER_REQUEST_MAX_ATTEMPTS,
      baseDelay: PLAYER_REQUEST_BASE_DELAY,
      backoffFactor: PLAYER_REQUEST_BACKOFF_FACTOR,
      fuzzFactor: PLAYER_REQUEST_FUZZ_FACTOR,
    },
    lowLatencyBufferConfig: {
      backward: LL_BUFFER_BACKWARD,
      forward: LL_BUFFER_FORWARD,
    },
    lowLatency: {
      targetLatency: LL_TARGET_LATENCY,
      catchup: {
        playbackRate: LL_CATCHUP_PLAYBACK_RATE,
        playbackRateThreshold: LL_CATCHUP_PLAYBACK_RATE_THRESHOLD,
        seekThreshold: LL_CATCHUP_SEEK_THRESHOLD,
      },
      fallback: {
        playbackRate: LL_FALLBACK_PLAYBACK_RATE,
        playbackRateThreshold: LL_FALLBACK_PLAYBACK_RATE_THRESHOLD,
        seekThreshold: LL_FALLBACK_SEEK_THRESHOLD,
      },
    },
  },
  userCountry: DEFAULT_COUNTRY || Country.COLOMBIA,
  welcomePage: {
    displayTime: 0,
    loginButton: <FormattedMessage id="welcomePage.loginButton.title" />,
    loginTitle: <FormattedMessage id="welcomePage.url.loginTitle" />,
    registerTitle: <FormattedMessage id="common.goTo" />,
  },
};

export const WELCOME_PAGE_GENERIC_IMAGE = {
  image: getJsonImage('welcomePageGeneric') || welcomePageGenericImage,
  title: <FormattedMessage id="welcomePage.genericImage.title" />,
  subtitle: <FormattedMessage id="welcomePage.genericImage.subtitle" />,
};

export const ConnectionType = {
  WIFI: 'WiFi',
  ETHERNET: 'Ethernet',
  CELLULAR: 'Cellular',
  BLUETOOTH: 'Bluetooth',
  WIMAX: 'WiMax',
};

export const SOURCE_DRM_QUALITY = {
  lowLatency: 'LL-HD',
  regular: 'SD-HD',
};

export const colors = {
  1: 'avatar-bg-1',
  2: 'avatar-bg-2',
  3: 'avatar-bg-3',
  4: 'avatar-bg-4',
  5: 'avatar-bg-5',
};

const Region = {
  BRAZIL: 'brazil',
  HISPAM: 'hispam',
};

export const COUNTRY_TO_REGION_MAP = {
  [Country.ARGENTINA]: Region.HISPAM,
  [Country.BRAZIL]: Region.BRAZIL,
  [Country.CHILE]: Region.HISPAM,
  [Country.COLOMBIA]: Region.HISPAM,
  [Country.ECUADOR]: Region.HISPAM,
  [Country.MEXICO]: Region.HISPAM,
  [Country.PERU]: Region.HISPAM,
  [Country.URUGUAY]: Region.HISPAM,
};

/**
 * @enum {string}
 * @readonly
 */
export const CountryLanguages = {
  [Country.ARGENTINA]: Locale.ES,
  [Country.BRAZIL]: Locale.PT,
  [Country.CHILE]: Locale.ES,
  [Country.COLOMBIA]: Locale.ES,
  [Country.ECUADOR]: Locale.ES,
  [Country.MEXICO]: Locale.ES,
  [Country.PERU]: Locale.ES,
  [Country.URUGUAY]: Locale.ES,
};

export const TYPE_ACTIONS_PROFILE = {
  CREATE: 'create',
  EDIT: 'edit',
  DELETE: 'delete',
  ONBOARDING: 'onboarding',
};

export const STORAGE_COUNTRY = 'country';

export const SKY_MAIS_TITLE = 'Sky+';

export const DEFAULT_COUNTRY_USER = DEFAULT_COUNTRY || Country.COLOMBIA;

export const REGIONS = {
  br: 'br',
  ssla: 'ssla',
};

export const BR_AGE_RATING_TYPE = ['AL', 'A10', 'A12', 'A14', 'A16', 'A18'];

export const AgeRatingBR = {
  NO_RATING: '0',
  TEN: '10',
  TWELVE: '12',
  FOURTEEN: '14',
  SIXTEEN: '16',
  EIGHTEEN: '18',
  DEFAULT_AGE: '18',
};

export const BR_AGE_RATING_DATA = {
  1: AgeRatingBR.NO_RATING,
  2: AgeRatingBR.TEN,
  3: AgeRatingBR.TWELVE,
  4: AgeRatingBR.FOURTEEN,
  5: AgeRatingBR.SIXTEEN,
  6: AgeRatingBR.EIGHTEEN,
};

export const BR_AGE_RATING_LABELS = {
  0: 'Livre',
  10: 'Até 10 anos',
  12: 'Até 12 anos',
  14: 'Até 14 anos',
  16: 'Até 16 anos',
  18: 'Maiores de 18 anos',
};

export const BR_DEFAULT_RATING_STEP = Object.values(BR_AGE_RATING_DATA).pop();

export const BR_GET_LAST_RATING = Object.keys(BR_AGE_RATING_DATA).length;

// warning texts period of coexistence between dgo and skymais
export const STORAGE_SHOW_WARNING_DGO_SKYMAIS = 'showWarningDgoSkymais';

export const WARNING_TEXTS_DGO_SKYMAIS = {
  firstWarning: {
    tizen: {
      title: <FormattedMessage id="modal.firstWarning.title" defaultMessage="A DGO agora vai virar Sky+" />,
      description: <FormattedMessage id="modal.firstWarning.tizen.description" defaultMessage="A mudança será em breve e você vai poder curtir seus conteúdos favoritos da TV por assinatura e do streaming por aqui." />,
      textButton: <FormattedMessage id="modal.button.ok" defaultMessage="Ok, entendi" />,
    },
    default: {
      title: <FormattedMessage id="modal.secondWarning.title" defaultMessage="A DGO agora é Sky+" />,
      description: <FormattedMessage id="modal.firstWarning.description" defaultMessage="Você pode curtir seus conteúdos favoritos da TV por assinatura e do streaming aqui ou baixar o app Sky+." />,
      textButton: <FormattedMessage id="modal.button.ok" defaultMessage="Ok, entendi" />,
    },
  },
  secondWarning: {
    tizen: {
      title: <FormattedMessage id="modal.secondWarning.title" defaultMessage="A DGO agora é Sky+" />,
      description: <FormattedMessage id="modal.secondWarning.tizen.description" defaultMessage="Curta seus conteúdos favoritos da TV por assinatura e do streaming por aqui." />,
      textButton: <FormattedMessage id="modal.button.ok" defaultMessage="Ok, entendi" />,
    },
    default: {
      title: <FormattedMessage id="modal.secondWarning.title" defaultMessage="A DGO agora é Sky+" />,
      description: <FormattedMessage id="modal.secondWarning.description" defaultMessage="Pra continuar curtindo seus conteúdos favoritos da TV por assinatura e do streaming, você precisa baixar o app Sky+." />,
      textButton: <FormattedMessage id="modal.button.logout" defaultMessage="Sair do app" />,
    },
  },
};

export const DGO_GRAN_HERMANO_TEXTS = {
  modal: {
    title: <FormattedMessage id="modal.granHermano.title" />,
    description: <FormattedMessage id="modal.granHermano.description" />,
    textButton: <FormattedMessage id="modal.granHermano.button" />,
  },
};

export const TAPPP_MODAL_STEPS_MESSAGES = {
  stepOne: {
    title: <FormattedMessage id="modal.tappp.stepOne.title" defaultMessage="Ahora puedes apostar viendo tus deportes favoritos" />,
    description: <FormattedMessage id="modal.tappp.stepOne.description" defaultMessage="Puedes acceder al panel de apuestas desde el player en las transmisiones con la etiqueta Interactivo." />,
    textButton: <FormattedMessage id="modal.tappp.stepOne.button" defaultMessage="Continuar" />,
  },
  stepTwo: {
    title: <FormattedMessage id="modal.tappp.stepTwo.title" defaultMessage="Completa tus apuestas deportivas con la app DGO" />,
    description: <FormattedMessage id="modal.tappp.stepTwo.description" defaultMessage="La experiencia de apuestas continúa en tu celular o\ntablet, donde puedes gestionar permisos en la app DGO. Descárgala en App Store o Play Store." />,
    textButton: <FormattedMessage id="modal.tappp.stepTwo.button" defaultMessage="Entiendo" />,
  },
};

export const STORAGE_SHOW_MODAL_GRAN_HERMANO = 'listProfilesWereShownGranHermanoModal';

// to use in google analytics with new users coming gran hermano
export const FREE_ACCOUNT = 'free-account';

export const BUSINESS_CAMPAIGN = new Set([
  'Gran Hermano',
]);

export const UPSELL_SHOW_BANNER_PAGES = new Set([
  '/private/home',
  '/private/lander/granhermano',
]);

const OKAY_TRANSLATION_ID = 'profile.error.ok';

// UpSell Messages
export const MessagesUpSellBrazilUser = {
  [FORGEROCK_ACCOUNT_TYPES.BR_OTT]: {
    title: 'player.error.entitlementsUpsell.userOTT_DTH_TVE_Brasil.title',
    subtitle: 'player.error.entitlementsUpsell.userOTT_Brasil.description',
    button: OKAY_TRANSLATION_ID,
    link: WEB_URL + UPSELL_URL_OTT_BRASIL,
  },
  [FORGEROCK_ACCOUNT_TYPES.SKY_DTH]: {
    title: 'player.error.entitlementsUpsell.userOTT_DTH_TVE_Brasil.title',
    subtitle: 'player.error.entitlementsUpsell.userDTH_TVE_Brasil.description',
    button: OKAY_TRANSLATION_ID,
    link: SKYMAIS_BASE_URL + UPSELL_URL_DTH_TVE_BRASIL,
  },
  [FORGEROCK_ACCOUNT_TYPES.MSO_DTH]: {
    title: 'player.error.entitlementsUpsell.userMSO_Brasil.title',
    subtitle: 'player.error.entitlementsUpsell.userMSO_Brasil.description',
    button: OKAY_TRANSLATION_ID,
  },
};

export const MessagesUpSellSSLAUser = {
  [FORGEROCK_ACCOUNT_TYPES.SSLA_OTT]: {
    title: 'player.error.entitlementsUpsell.userOTT_DTH_TVE_SSLA.title',
    subtitle: 'player.error.entitlementsUpsell.userOTT_SSLA.description',
    button: OKAY_TRANSLATION_ID,
    link: OTT_SSLA_BASE_URL,
  },
  [FORGEROCK_ACCOUNT_TYPES.SSLA_DTH]: {
    title: 'player.error.entitlementsUpsell.userOTT_DTH_TVE_SSLA.title',
    subtitle: 'player.error.entitlementsUpsell.userDTH_TVE_SSLA.description',
    button: OKAY_TRANSLATION_ID,
    link: OTT_DTH_TVE_SSLA_BASE_URL,
  },
  [FORGEROCK_ACCOUNT_TYPES.MSO_DTH]: {
    title: 'player.error.entitlementsUpsell.userMSO_SSLA.title',
    subtitle: 'player.error.entitlementsUpsell.userMSO_SSLA.description',
    button: OKAY_TRANSLATION_ID,
  },
};

export const MessagesUpSellUserB2B2C = {
  userB2B2C: {
    title: 'player.error.entitlementsUpsell.userB2B2C.title',
    subtitle: 'player.error.entitlementsUpsell.userB2B2C.description',
    button: 'common.not.now',
    link: ' ',
  },
  userB2B2CWithoutSelfcareLink: {
    title: 'player.error.entitlementsUpsell.userB2B2C.noLink.title',
    button: 'common.got.it',
  },
};

export const MessagesUpSellGranHermanoUser = {
  userGranHermanoArgentina: {
    title: 'player.error.entitlementsUpsell.userGranHermanoArgentina.title',
    subtitle: 'player.error.entitlementsUpsell.userGranHermanoArgentina.description',
    button: OKAY_TRANSLATION_ID,
    link: OTT_SSLA_BASE_URL,
    qrLink: OTT_SSLA_BASE_URL,
  },
  userGranHermanoSSLA: {
    title: 'player.error.entitlementsUpsell.userGranHermanoSSLA.title',
    subtitle: 'player.error.entitlementsUpsell.userGranHermanoSSLA.description',
    button: OKAY_TRANSLATION_ID,
    link: OTT_SSLA_BASE_URL,
  },
  userOTT_DTH_TVE_Argentina: {
    title: 'player.error.entitlementsUpsell.userOTT_DTH_TVE_Argentina.title',
    subtitle: 'player.error.entitlementsUpsell.userOTT_DTH_TVE_Argentina.description',
    button: OKAY_TRANSLATION_ID,
    link: OTT_DTH_TVE_SSLA_BASE_URL,
  },
  bannerGranHermanoArgentina: {
    message: 'player.banner.entitlementsUpsell.userGranHermanoArgentina.message',
    link: OTT_SSLA_BASE_URL,
  },
  bannerGranHermanoSSLA: {
    message: 'player.banner.entitlementsUpsell.userGranHermanoSSLA.message',
    link: OTT_SSLA_BASE_URL,
  },
};

export const MessagesBannerUpSellGranHermano = {
  userArgentina: {
    title: <FormattedMessage id="banner.upsell.granHermanoArgentina.title" />,
    subtitle: <FormattedMessage id="banner.upsell.granHermanoArgentina.subtitle" />,
  },
  userLatam: {
    title: <FormattedMessage id="banner.upsell.granHermanoLatam.title" />,
    subtitle: <FormattedMessage id="banner.upsell.granHermanoLatam.subtitle" />,
  },
};

export const WANT_TO_LEAVE_TEXT = () => {
  const appTheme = import.meta.env.VITE_THEME;
  const values = {
    ...((appTheme === 'dgo') ? { app: 'DGO' } : { app: 'Sky+' }),
  };
  return {
    modal: {
      title: <FormattedMessage id="modal.wantToLeave.title" values={values} />,
      buttonLeave: <FormattedMessage id="modal.wantToLeave.buttonLeave" values={values} />,
      buttonNotNow: <FormattedMessage id="modal.wantToLeave.buttonNotNow" values={values} />,
    },
  };
};

export const OTT_SSLA_DOMAINS = {
  gh: '/gh',
  ya: '/ya',
};

export const DTH_OTT_SSLA_DOMAINS = {
  AR: '.ar',
  CL: '.cl',
  EC: '.ec',
  UY: '.uy',
  PE: '.pe',
  CO: '.co',
};

export const SELECTED_CAROUSEL_CARD_KEY = 'selectedCarouselCardKey';

export const STARTED_PAGE_ID = 'startedPageId';

export const PROFILE_VIEWED_INTERACTIVITY_BUTTON = 'profileViewedInteractivityButton';

export const PROFILES_WERE_SHOWN_INTERACTIVITY_BUTTON = 'listProfilesWereShownInteractivityButton';

export const TAPPP_MODAL_VIEW_STORAGED = 'listProfilesWereShownTapppModal';

export const SHOW_TAPPP_MODAL_STORAGED = 'showTapppModal';

export const KEY_MOMENTS_ONBOARDING_COMPLETE_KEY = 'keyMomentsOnboardingComplete';

export const KEY_MOMENTS_ONBOARDING_LAST_SHOWN_KEY = 'keyMomentsOnboardingLastShown';

export const TAPPP_CONFIG_DATA = {
  broadcasterName: 'DTVLA',
  env: TAPPP_ENV || 'sandbox',
  deviceType: 'tvApp',
  sdkScriptId: 'run-time',
  allUsersAllowed: 'allUsersAllowed',
};

export const NEW_EPG_CONFIG_DATA = {
  allUsersAllowed: 'allUsersAllowed',
};

export const PLAYER_CONFIG_OVERRIDE_DATA = {
  allSystemsAllowed: 'allSystemsAllowed',
};

export const DEFAULT_AGE_RATING = '18';
export { IS_MY_LIST_ENABLED as isMyListEnabled } from './env';

export const valuesAllChannelsCatchup = 'allChannels';
